import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Dialog } from 'sharedUi/Dialog';
import { Toast } from 'sharedUi/Toast';
import { Menu } from 'primereact/menu';
import styles from './units-list.module.css';
import type { MenuItem } from 'primereact/menuitem';
import { Tooltip } from 'sharedUi/Tooltip';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { unitsService, type UnitQueryParams } from '../../services/units.service';
import { districtsService } from '../../services/districts.service';
import { titleCase } from '../../utils/display-case';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { Unit, District } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'name', header: 'Unit Name', filter: true }];

// Type for selected unit in kebab menu
interface SelectedUnitForMenu {
  _id: string;
  name: string;
  departmentId?: string;
  districtId?: string;
  appId?: string | null;
}

const UnitsList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const navigate = useAppNavigate();
  const { navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'units',
    basePath: '/units',
  });
  const toast = useRef<Toast>(null);
  const menuRef = useRef<Menu>(null);
  const canCreate = useCanCreate(JOB_NAMES.UNITS) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.UNITS);
  const canDelete = useCanDelete(JOB_NAMES.UNITS);

  // Tab state

  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnitForMenu, setSelectedUnitForMenu] = useState<SelectedUnitForMenu | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null);
  const [searchText, setSearchText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);

  // Per-column filters
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  // Districts for lookups
  const [districts, setDistricts] = useState<District[]>([]);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);


  // Unit Head data map: unitId -> unit head assigned user name
  const [unitHeadMap, setUnitHeadMap] = useState<Map<string, string>>(new Map());
  const [unitHeadLoading, setUnitHeadLoading] = useState<Set<string>>(new Set());

  // Unit Details Dialog state
  const [unitDialogVisible, setUnitDialogVisible] = useState(false);
  const [unitDialogDetails, setUnitDialogDetails] = useState<Unit | null>(null);
  const [unitDialogLoading, setUnitDialogLoading] = useState(false);

  // Fetch districts for dropdown (no pagination)
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [districtsData, projectsData] = await Promise.all([
          districtsService.getAllForDropdown(),
          projectsService.getAccessible(),
        ]);
        setDistricts(districtsData);
        setAccessibleProjects(projectsData);
      } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  // Districts are project-scoped (each carries an appId; null = global/shared). When a
  // project is selected, the District filter must offer only that project's districts
  // (plus global ones), not every district across all projects.
  const visibleDistricts = useMemo(
    () =>
      selectedProjectId
        ? districts.filter((d) => !d.appId || d.appId === selectedProjectId)
        : districts,
    [districts, selectedProjectId]
  );

  // Drop a selected district that no longer belongs to the chosen project, so a stale
  // filter from a previous project can't silently keep narrowing the list.
  useEffect(() => {
    if (
      selectedDistrict &&
      selectedProjectId &&
      selectedDistrict.appId &&
      selectedDistrict.appId !== selectedProjectId
    ) {
      setSelectedDistrict(null);
    }
  }, [selectedProjectId, selectedDistrict]);

  const fetchUnits = useCallback(
    async (
      page: number = 1,
      size: number = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);

        // Build query parameters for server-side pagination
        const params: UnitQueryParams = {
          page,
          page_size: size,
        };

        // Only add include_deleted if it's true
        if (includeDeleted) {
          params.include_deleted = true;
        }

        // Use districtId from selected district
        if (selectedDistrict?._id) {
          params.districtId = selectedDistrict._id;
        }

        if (selectedProjectId) {
          params.app_id = selectedProjectId;
        }

        params.include_global = showCore;

        // Add search parameter for server-side search (using debounced value)
        if (debouncedSearchText?.trim()) {
          params.search = debouncedSearchText.trim();
        }

        // Add sort parameters for server-side sorting
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }

        // Add per-column filters when present
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        const response = await unitsService.getAll(params);

        // Map _id to id for DataGrid compatibility
        const mappedData = (response.data || []).map(unit => ({ ...unit, id: unit._id }));

        setUnits(mappedData);
        // Handle both totalItems and total for backward compatibility
        const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
        setTotalRecords(total);
      } catch (error: any) {
        console.error('Failed to fetch units:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: extractErrorMessage(error, 'Failed to load units. Please try again.'),
          life: 10000,
        });
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, selectedDistrict, selectedProjectId, showCore, debouncedSearchText, pageSize, sortField, sortOrder, filters]
  );

  // Handle DataTable pagination changes
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchUnits(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort event - triggers server-side sorting
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchUnits(1, pageSize, newSortField, newSortOrder);
  };

  // Handle per-column filter changes
  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchUnits(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page and fetch when filters or sort change
  useEffect(() => {
    setFirst(0);
    fetchUnits(1, pageSize);
  }, [includeDeleted, selectedDistrict, selectedProjectId, showCore, debouncedSearchText]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch unit head data for each unit in the list
  useEffect(() => {
    const fetchUnitHeads = async () => {
      if (units.length === 0) return;

      // Get unit IDs that are not already in the map or loading
      const unitIdsToFetch = units
        .filter(unit => !unitHeadMap.has(unit._id) && !unitHeadLoading.has(unit._id))
        .map(unit => unit._id);

      if (unitIdsToFetch.length === 0) return;

      // Mark as loading
      setUnitHeadLoading(prev => {
        const newSet = new Set(prev);
        unitIdsToFetch.forEach(id => newSet.add(id));
        return newSet;
      });

      // Fetch unit details for each unit
      const results = await Promise.allSettled(
        unitIdsToFetch.map(id => unitsService.getById(id))
      );

      // Process results
      const newHeadMap = new Map(unitHeadMap);
      results.forEach((result, index) => {
        const unitId = unitIdsToFetch[index];
        if (result.status === 'fulfilled') {
          const unitDetails = result.value;
          // Find post where isUnitHead is true
          const unitHeadPost = unitDetails.posts?.find((post: any) => post.isUnitHead && !post.isDelete);
          if (unitHeadPost?.assignedUser) {
            const userName = typeof unitHeadPost.assignedUser === 'object'
              ? unitHeadPost.assignedUser.name
              : unitHeadPost.assignedUser;
            newHeadMap.set(unitId, userName || '-');
          } else {
            newHeadMap.set(unitId, '-');
          }
        } else {
          newHeadMap.set(unitId, '-');
        }
      });

      setUnitHeadMap(newHeadMap);

      // Remove from loading
      setUnitHeadLoading(prev => {
        const newSet = new Set(prev);
        unitIdsToFetch.forEach(id => newSet.delete(id));
        return newSet;
      });
    };

    fetchUnitHeads();
  }, [units]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedUnitId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUnitId) return;

    try {
      setDeleteLoading(true);
      await unitsService.delete(selectedUnitId);
      setDeleteDialogOpen(false);
      setSelectedUnitId(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Unit deleted successfully',
        life: 3000,
      });
      fetchUnits(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      console.error('Failed to delete unit:', error);
      setDeleteDialogOpen(false);
      setSelectedUnitId(null);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(error, 'Failed to delete unit'),
        life: 10000,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedUnitId(null);
  };


  // Kebab menu handlers
  const handleAddChildUnit = (unit: SelectedUnitForMenu) => {
    const params = new URLSearchParams({
      parentUnitId: unit?._id,
      parentUnitName: unit?.name,
    });
    if (unit?.departmentId) params.append('departmentId', unit?.departmentId);
    if (unit?.districtId) params.append('districtId', unit?.districtId);
    navigate(`/units/create?${params.toString()}`);
  };

  // Handle unit name click - open details dialog
  const handleUnitNameClick = async (unitId: string) => {
    setUnitDialogVisible(true);
    setUnitDialogLoading(true);
    try {
      const unitDetails = await unitsService.getById(unitId);
      setUnitDialogDetails(unitDetails);
    } catch (err) {
      console.error('Failed to fetch unit details:', err);
      setUnitDialogDetails(null);
    } finally {
      setUnitDialogLoading(false);
    }
  };

  // Close unit details dialog
  const handleUnitDialogClose = () => {
    setUnitDialogVisible(false);
    setUnitDialogDetails(null);
  };

  // Build menu items based on RBAC permissions - shows all actions when > 3
  const getMenuItems = (): MenuItem[] => {
    if (!selectedUnitForMenu) return [];

    const items: MenuItem[] = [
      {
        label: 'View',
        icon: 'pi pi-eye',
        className: 'action-menu-view',
        command: () => handleView(selectedUnitForMenu._id),
      },
    ];

    if (canUpdate) {
      items.push({
        label: 'Edit',
        icon: 'pi pi-pencil',
        className: 'action-menu-edit',
        command: () => handleEdit(selectedUnitForMenu._id),
      });
    }

    if (canDelete) {
      items.push({
        label: 'Delete',
        icon: 'pi pi-trash',
        className: 'action-menu-delete',
        command: () => handleDeleteClick(selectedUnitForMenu._id),
      });
    }

    if (canCreate) {
      items.push({
        label: 'Add Child Unit',
        icon: 'pi pi-plus-circle',
        className: 'action-menu-add',
        command: () => handleAddChildUnit(selectedUnitForMenu),
      });
    }

    return items;
  };

  // Memoize columns for SaasDataTable
  const columns: SaasColumn<Unit>[] = useMemo(() => {
    const baseColumns: SaasColumn<Unit>[] = [
      {
        field: 'name',
        header: 'Unit Name',
        sortable: true,
        filter: true,
        hideable: false,
        body: (rowData: Unit) => (
          <span
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer font-medium"
            onClick={() => handleUnitNameClick(rowData._id)}
            title={rowData.name}
          >
            {/* Cased for READING only — the stored name is what the tooltip shows,
                and what search, sort and export still work on. */}
            {titleCase(rowData.name)}
          </span>
        ),
      },
      {
        field: 'email',
        header: 'Email',
        sortable: true,
        body: (rowData: Unit) => rowData.email || '-',
      },
      {
        /* The district the unit sits in. The API populates `district` on the row;
           where it has not (an older record, a lighter list projection) the id is
           resolved against the districts this screen already loads for its filter,
           so the column reads the same either way. */
        field: 'districtId',
        header: 'District',
        sortable: true,
        body: (rowData: Unit) => {
          const populated = (rowData as unknown as { district?: { name?: string } }).district?.name;
          const name = populated || districts.find((d) => d._id === rowData.districtId)?.name;
          return name ? titleCase(name) : '-';
        },
      },
      {
        field: 'appId',
        header: 'Project',
        body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
      },
      {
        field: 'parentUnit.name',
        header: 'Parent Unit',
        sortable: true,
        body: (rowData: Unit) => rowData.parentUnit?.name || '-',
      },
    ];

    // Always add actions column at the end
    baseColumns.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (rowData: Unit) => {
        const isDeleted = (rowData as any).isDelete || rowData.isDeleted;

        // Count available actions
        let actionCount = 1; // View is always available
        if (!isDeleted) {
          if (canUpdate) actionCount++;
          if (canDelete) actionCount++;
          if (canCreate) actionCount++; // Add Child Unit
        }

        // If more than 3 actions, show all in menu
        if (actionCount > 3) {
          return (
            <div className="flex justify-center">
              <button
                className="p-2 cursor-pointer"
                style={{ background: 'none', border: 'none', outline: 'none' }}
                onClick={(e) => {
                  setSelectedUnitForMenu({
                    _id: rowData._id,
                    name: rowData.name,
                    departmentId: rowData?.departmentId,
                    districtId: rowData?.districtId,
                    appId: (rowData as any)?.appId ?? null,
                  });
                  menuRef.current?.toggle(e);
                }}
                data-testid="UnitsList.Action.Menu"
                data-pr-tooltip="Actions"
              >
                <i className="pi pi-ellipsis-v text-ink-muted hover:text-gray-800 dark:hover:text-gray-200" style={{ fontSize: '1.125rem' }} />
              </button>
              <Tooltip target="[data-pr-tooltip]" />
            </div>
          );
        }

        // Show up to 3 icons directly
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 cursor-pointer"
              style={{ background: 'none', border: 'none', outline: 'none' }}
              onClick={() => handleView(rowData._id)}
              data-testid="UnitsList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200" style={{ fontSize: '1.125rem' }} />
            </button>
            <Tooltip target="[data-pr-tooltip]" />

            {!isDeleted && canUpdate && (
              <button aria-label="Edit"
                className="p-2 cursor-pointer"
                style={{ background: 'none', border: 'none', outline: 'none' }}
                onClick={() => handleEdit(rowData._id)}
                data-testid="UnitsList.Action.Edit"
                data-pr-tooltip="Edit"
              >
                <i className="pi pi-pencil text-blue-600 hover:text-blue-800" style={{ fontSize: '1.125rem' }} />
              </button>
            )}

            {!isDeleted && canDelete && (
              <button aria-label="Delete"
                className="p-2 cursor-pointer"
                style={{ background: 'none', border: 'none', outline: 'none' }}
                onClick={() => handleDeleteClick(rowData._id)}
                data-testid="UnitsList.Action.Delete"
                data-pr-tooltip="Delete"
              >
                <i className="pi pi-trash text-red-600 hover:text-red-800" style={{ fontSize: '1.125rem' }} />
              </button>
            )}

            {/* Show Add Child Unit as icon if it's the 3rd action (when no edit or delete permission) */}
            {!isDeleted && canCreate && actionCount <= 3 && (
              <button aria-label="Add"
                className="p-2 cursor-pointer"
                style={{ background: 'none', border: 'none', outline: 'none' }}
                onClick={() => handleAddChildUnit({
                  _id: rowData._id,
                  name: rowData.name,
                  departmentId: rowData?.departmentId,
                  districtId: rowData?.districtId,
                })}
                data-testid="UnitsList.Action.AddChild"
                data-pr-tooltip="Add Child Unit"
              >
                <i className="pi pi-plus-circle text-purple-600 hover:text-purple-800" style={{ fontSize: '1.125rem' }} />
              </button>
            )}
          </div>
        );
      },
    });

    return baseColumns;
  }, [canUpdate, canDelete, canCreate, includeDeleted, unitHeadMap, unitHeadLoading, accessibleProjects, districts]);

  return (
    <PermissionGuard jobName={JOB_NAMES.UNITS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-Units-List">
        {/* Page Header — when embedded in the group tab, PageHeader hides the
            title/subtitle but keeps the Create Unit action (see PageHeader). */}
        <PageHeader
          embedded={embedded}
          icon="pi pi-building"
          title="Units Management"
          subtitle="Manage police units, hierarchies, and organizational structure"
          testId="Units.Header"
        />

        {/* Main Card with Tabs */}
        <div className={styles.card}>
          <div className={styles.cardContent}>
            <div className={styles.tabContent}>
                  <SaasDataTable
                    tableId="units-v2"
                    columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
                    data={units}
                    loading={loading}
                    dataKey="_id"
                    emptyMessage="No units found"
                    first={first}
                    rows={pageSize}
                    totalRecords={totalRecords}
                    onPage={handlePageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sortField={sortField || undefined}
                    sortOrder={sortOrder || undefined}
                    onSort={handleSort}
                    filters={filters}
                    onFilter={handleFilter}
                    globalSearch={searchText}
                    onGlobalSearchChange={setSearchText}
                    globalSearchPlaceholder="Search by name or code..."
                    testId="UnitsList"
                    toolbarStart={
                      <div className="flex items-center gap-2 flex-wrap">
                        {!appId && accessibleProjects.length > 1 && (
                          <Dropdown
                            value={selectedProjectId}
                            options={accessibleProjects.map(p => ({
                              label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                              value: p._id,
                            }))}
                            onChange={(e: any) => setSelectedProjectId(e.value || null)}
                            placeholder="Project"
                            showClear
                            style={{ minWidth: '160px', width: '160px' }}
                            data-testid="UnitsList.Filter.Project"
                            resetFilterOnHide={true}
                          />
                        )}

                        <Dropdown
                          value={selectedDistrict?._id || null}
                          options={visibleDistricts.map(d => ({ label: d.name, value: d._id }))}
                          onChange={(e: any) => {
                            const district = visibleDistricts.find(d => d._id === e.value);
                            setSelectedDistrict(district || null);
                          }}
                          placeholder="District"
                          showClear
                          style={{ minWidth: '150px', width: '150px' }}
                          data-testid="UnitsList.Filter.District"
                          resetFilterOnHide={true}
                        />

                        <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="Unit.Toggle.ShowCore" />
                      </div>
                    }
                    toolbarEnd={
                      <>
                        <span className="text-sm text-ink-muted whitespace-nowrap">
                          {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                        </span>
                        <RefreshButton
                          onClick={() => fetchUnits(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                          loading={loading}
                          testId="UnitsList.Button.Refresh"
                        />
                        {canCreate && (
                          <Button
                            label="Create Unit"
                            icon="pi pi-plus"
                            onClick={() => navigate('/units/create')}
                            testId="UnitsList.Button.Create"
                            size="small"
                          />
                        )}
                      </>
                    }
                  />
            </div>
          </div>
        </div>

        {/* Kebab Menu for additional actions */}
        <Menu
          ref={menuRef}
          model={getMenuItems()}
          popup
          popupAlignment="right"
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this unit? This action will mark the unit as deleted."
          testId="UnitsList.Dialog.Delete"
          loading={deleteLoading}
        />

        {/* Unit Details Dialog */}
        <Dialog
          visible={unitDialogVisible}
          onHide={handleUnitDialogClose}
          header={
            <div className="flex items-center justify-between w-full pr-8">
              {/* Left - Title */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                  <i className="pi pi-building text-white" style={{ fontSize: '1.25rem' }} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-ink">
                    {unitDialogDetails?.name || 'Unit Details'}
                  </h2>
                  {(unitDialogDetails as any)?.hierarchyLevel && (
                    <span className="text-xs text-ink-muted">
                      {typeof (unitDialogDetails as any).hierarchyLevel === 'object'
                        ? (unitDialogDetails as any).hierarchyLevel.name
                        : (unitDialogDetails as any).hierarchyLevel}
                    </span>
                  )}
                </div>
              </div>
            </div>
          }
          style={{ width: '560px', maxWidth: '92vw' }}
          modal
          className="unit-details-dialog"
          contentStyle={{ padding: '1.5rem', maxHeight: '85vh', overflow: 'auto' }}
        >
          {unitDialogLoading ? (
            <div className="flex items-center justify-center py-12">
              <i className="pi pi-spin pi-spinner text-2xl text-blue-500" />
            </div>
          ) : unitDialogDetails ? (
            <div>
              {/* Hierarchy Path only */}
              <div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2 pb-2 border-b border-line">
                    <i className="pi pi-sitemap text-purple-500" />
                    Hierarchy Path
                  </h3>
                  {(() => {
                    // Build hierarchy path from parentUnit chain
                    const buildHierarchyPath = (unit: Unit): { id: string; name: string; level?: string }[] => {
                      const path: { id: string; name: string; level?: string }[] = [];
                      let current: any = unit;

                      // Traverse up the parent chain
                      while (current) {
                        path.unshift({
                          id: current._id,
                          name: current.name,
                          level: typeof current.hierarchyLevel === 'object'
                            ? current.hierarchyLevel?.name
                            : current.hierarchyLevel,
                        });
                        current = current.parentUnit;
                      }

                      return path;
                    };

                    const hierarchyPath = buildHierarchyPath(unitDialogDetails);

                    return hierarchyPath.length > 0 ? (
                      <div className="space-y-1 max-h-[450px] overflow-y-auto pr-2">
                        {hierarchyPath.map((node, index) => (
                          <div key={node.id} className="flex items-center">
                            {/* Indentation */}
                            <div style={{ width: `${index * 16}px` }} />
                            {/* Connector line */}
                            {index > 0 && (
                              <div className="flex items-center mr-2">
                                <div className="w-3 h-px bg-gray-300 dark:bg-gray-600" />
                              </div>
                            )}
                            {/* Node */}
                            <div
                              className={`flex items-center gap-2 px-3 py-2 rounded-md ${
                                node.id === unitDialogDetails._id
                                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                  : index === 0
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium'
                                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              }`}
                                style={{ fontSize: '12px' }}
                              >
                              <i className={`pi ${index === hierarchyPath.length - 1 ? 'pi-map-marker' : 'pi-building'}`} style={{ fontSize: '12px' }} />
                              <span>{node.name}</span>
                              {node.level && (
                                <span className="text-ink-muted" style={{ fontSize: '12px' }}>
                                  ({node.level})
                              </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-sm text-ink-muted py-4 text-center">
                      No hierarchy data
                    </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-ink-muted">
              Failed to load unit details
            </div>
          )}
        </Dialog>

      </div>
    </PermissionGuard>
  );
};

export default UnitsList;
